// ===========================================
// Create Project Modal Component
// Simplified to essential fields only
// ===========================================

import React, { useState } from 'react';
import { useProjects } from '../context/ProjectContext';
import { X, Loader, Plus } from 'lucide-react';
import './Modal.scss';

const CreateProjectModal = ({ onClose, onCreated }) => {
  const { createProject } = useProjects();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state - simplified to essential fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'idea',
    visibility: 'private',
    tags: '',
    repoUrl: '',
    liveUrl: ''
  });

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate project name is required
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }
    
    setLoading(true);
    
    // Parse comma-separated tags into array
    const projectData = {
      ...formData,
      tags: formData.tags 
        ? formData.tags.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
        : []
    };
    
    const result = await createProject(projectData);
    
    if (result.success) {
      onCreated(result.data);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal__header">
          <h2>Create New Project</h2>
          <button className="modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form className="modal__form" onSubmit={handleSubmit}>
          {error && (
            <div className="modal__error">{error}</div>
          )}

          {/* Project Name - Required field */}
          <div className="modal__field">
            <label htmlFor="name">Project Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="My Awesome Project"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Description - Optional project summary */}
          <div className="modal__field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              placeholder="What is this project about?"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
              rows={3}
            />
          </div>

          {/* Status & Visibility Row */}
          <div className="modal__row">
            {/* Status - Current project stage */}
            <div className="modal__field">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="idea">Idea</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            {/* Visibility - Who can see this project */}
            <div className="modal__field">
              <label htmlFor="visibility">Visibility</label>
              <select
                id="visibility"
                name="visibility"
                value={formData.visibility}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="private">Private</option>
                <option value="internal">Internal (Team)</option>
                <option value="public">Public (Portfolio)</option>
              </select>
            </div>
          </div>

          {/* Tags - Comma-separated categorization */}
          <div className="modal__field">
            <label htmlFor="tags">Tags</label>
            <input
              id="tags"
              name="tags"
              type="text"
              placeholder="saas, fintech, ai (comma-separated)"
              value={formData.tags}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          {/* URLs Row - Github & Live Link */}
          <div className="modal__row">
            {/* Repository URL - Github link */}
            <div className="modal__field">
              <label htmlFor="repoUrl">Repository URL</label>
              <input
                id="repoUrl"
                name="repoUrl"
                type="url"
                placeholder="https://github.com/..."
                value={formData.repoUrl}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
            
            {/* Live URL - Deployed site link */}
            <div className="modal__field">
              <label htmlFor="liveUrl">Live URL</label>
              <input
                id="liveUrl"
                name="liveUrl"
                type="url"
                placeholder="https://..."
                value={formData.liveUrl}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          {/* Actions - Cancel and Submit buttons */}
          <div className="modal__actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Create Project
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProjectModal;
